import { Action, createReducer, on } from '@ngrx/store';

import { sourceActions } from './source.actions';
import * as interfaces from '../../interfaces';
import {
  assembleAnalyticsData,
  updateLoadingState,
  updateNestedState,
  updateNestedTableState,
  updateNestedTableVisibility
} from '../../helpers/store.helper';
import { defaultSorter } from '../../data';
import { ILabelValue } from '@shared/interfaces';
import { IAnalyticsNested, IAnalyticsRequest } from '@measurement-studio/interfaces';

export const allMediumsOption: ILabelValue = {
  label: 'measurementStudio.features.analytics.webActivities.sourceTable.selectorAllTypes',
  value: null,
};

export const sourceFeatureKey = 'source';

export interface ISourceState extends interfaces.IAnalyticsIsLoading {
  nested: IAnalyticsNested<Partial<interfaces.ISourceTableItem>>;
  totalResults: number;
  data: Partial<interfaces.ISourceTableItem>[];
  nestedTableState: interfaces.INestedTableState;
  usedParamsInDriver: IAnalyticsRequest | null;
  selectedMedium: ILabelValue;
}

export const initialState: ISourceState = {
  nested: {
    data: {},
    loading: {},
    totalResults: {},
    sorter: {},
    pager: {},
  },
  totalResults: 0,
  isDriverLoading: false,
  campaignIsLoading: false,
  responseIsLoading: false,
  attributedIsLoading: false,
  influenceIsLoading: false,
  returnsIsLoading: false,
  costIsLoading: false,
  data: [],
  nestedTableState: {
    visible: {},
  },
  usedParamsInDriver: null,
  selectedMedium: allMediumsOption,
};

const sourceReducer = createReducer(
  initialState,
  on(sourceActions.setUsedParams, (state, action) => ({
    ...state,
    usedParamsInDriver: action.request
  })),
  on(sourceActions.loadMetaDataSuccess, (state, action) => ({
    ...state,
    ...(action.parentId
      ? {nested: updateNestedState(state.nested, action, action.data.campaign)}
      : assembleAnalyticsData(state.data, action, action.data.campaign)),
    campaignIsLoading: false,
    isDriverLoading: false,
  })),
  on(sourceActions.loadResponsesSuccess, (state, action) => ({
    ...state,
    ...(action.parentId
      ? {nested: updateNestedState(state.nested, action, action.data.response)}
      : assembleAnalyticsData(state.data, action, action.data.response)),
    responseIsLoading: false,
    isDriverLoading: false,
  })),
  on(sourceActions.loadAttributionSuccess, (state, action) => ({
    ...state,
    ...(action.parentId
      ? {nested: updateNestedState(state.nested, action, action.data.attributed)}
      : assembleAnalyticsData(state.data, action, action.data.attributed)),
    attributedIsLoading: false,
    isDriverLoading: false,
  })),
  on(sourceActions.loadInfluenceSuccess, (state, action) => ({
    ...state,
    ...(action.parentId
      ? {nested: updateNestedState(state.nested, action, action.data.influenced)}
      : assembleAnalyticsData(state.data, action, action.data.influenced)),
    influenceIsLoading: false,
    isDriverLoading: false,
  })),
  on(sourceActions.loadReturnsSuccess, (state, action) => ({
    ...state,
    ...(action.parentId
      ? {nested: updateNestedState(state.nested, action, action.data.returns)}
      : assembleAnalyticsData(state.data, action, action.data.returns)),
    returnsIsLoading: false,
    isDriverLoading: false,
  })),
  on(sourceActions.loadCostsSuccess, (state, action) => ({
    ...state,
    ...(action.parentId
      ? {nested: updateNestedState(state.nested, action, action.data.costs)}
      : assembleAnalyticsData(state.data, action, action.data.costs)),
    costIsLoading: false,
    isDriverLoading: false,
  })),
  on(sourceActions.loadMetaData, (state, action) => ({
    ...state,
    campaignIsLoading: true,
    ...updateLoadingState(action, state.nested, true),
  })),
  on(sourceActions.loadResponses, (state, action) => ({
    ...state,
    responseIsLoading: true,
    ...updateLoadingState(action, state.nested, true),
  })),
  on(sourceActions.loadAttribution, (state, action) => ({
    ...state,
    attributedIsLoading: true,
    ...updateLoadingState(action, state.nested, true),
  })),
  on(sourceActions.loadInfluence, (state, action) => ({
    ...state,
    influenceIsLoading: true,
    ...updateLoadingState(action, state.nested, true),
  })),
  on(sourceActions.loadCosts, (state, action) => ({
    ...state,
    costIsLoading: true,
    ...updateLoadingState(action, state.nested, true),
  })),
  on(sourceActions.loadReturns, (state, action) => ({
    ...state,
    returnsIsLoading: true,
    ...updateLoadingState(action, state.nested, true),
  })),
  on(sourceActions.loadMetaDataFailure, (state) => ({
    ...state, campaignIsLoading: false, isDriverLoading: false
  })),
  on(sourceActions.loadResponsesFailure, (state) => ({
    ...state, responseIsLoading: false, isDriverLoading: false
  })),
  on(sourceActions.loadAttributionFailure, (state) => ({
    ...state, attributedIsLoading: false, isDriverLoading: false
  })),
  on(sourceActions.loadInfluenceFailure, (state) => ({
    ...state, influenceIsLoading: false, isDriverLoading: false
  })),
  on(sourceActions.loadCostsFailure, (state) => ({
    ...state, costIsLoading: false, isDriverLoading: false
  })),
  on(sourceActions.loadReturnsFailure, (state) => ({
    ...state, returnsIsLoading: false, isDriverLoading: false
  })),
  on(sourceActions.changeNestedSorting, (state, action) => {
    const nestedSorting = action.data.value.sortDirection ? action.data.value : defaultSorter;
    return {
      ...state,
      nested: updateNestedTableState(state.nested, 'sorter', action.data.id, nestedSorting),
    };
  }),
  on(sourceActions.changeNestedPagination, (state, action) => ({
    ...state,
    nested: updateNestedTableState(state.nested, 'pager', action.data.id, action.data.value),
  })),
  on(sourceActions.clearNestedState, (state) => ({
    ...state,
    nested: {
      data: {},
      loading: {},
      totalResults: {},
      sorter: {},
      pager: {},
    }
  })),
  on(sourceActions.closeNestedTables, (state) => ({
    ...state,
    nestedTableState: {visible: {}}
  })),
  on(sourceActions.toggleTableRow, (state, action) => ({
    ...state,
    nestedTableState: updateNestedTableVisibility(state.nestedTableState, action.id),
  })),
  on(sourceActions.setSelectedMedium, (state, action) => ({
    ...state,
    selectedMedium: action.data
  })),
);

export function reducer(state, action: Action): ISourceState {
  return sourceReducer(state, action);
}
